import React, { useState } from 'react';
import { Input, Button, Modal, Spin, Tag, Tabs, Radio, Checkbox, DatePicker } from 'antd';
import {
  StepBackwardOutlined, CaretLeftOutlined,
  CaretRightOutlined, StepForwardOutlined,
  PlusOutlined, EditOutlined, DeleteOutlined,
  SaveOutlined, CloseOutlined, SearchOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useEmployeeForm } from './hooks/useEmployeeForm';
import './P9101Form.css';

const P9101Form: React.FC = () => {
  const {
    mode, loading, statusLabel,
    f901_01, f901_11, f901_12, f901_13, f901_14,
    f901_19, f901_20, f901_25, f901_26, f901_27, f901_28,
    f901_30, f901_31, f901_32, f901_33, f901_34, f901_35, f901_37,
    f901_52, f901_55, f901_60,
    f901_91, f901_92,
    setF901_01, setF901_11, setF901_12, setF901_13, setF901_14,
    setF901_19, setF901_20, setF901_25, setF901_26, setF901_27, setF901_28,
    setF901_30, setF901_31, setF901_32, setF901_33, setF901_34, setF901_35, setF901_37,
    setF901_52, setF901_55, setF901_60,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save, doDelete,
    quickSearch, recordPosition, recordCount,
  } = useEmployeeForm();

  const [searchKey, setSearchKey] = useState('');
  const isEditing = mode === 'add' || mode === 'edit';

  const statusColor = statusLabel === '已離職' ? 'red' : statusLabel === '試用人員' ? 'orange' : 'green';

  return (
    <Spin spinning={loading}>
      <div className="p9101-form">
        <div className="p9101-header-row">
          <h2>員工資料</h2>
          {statusLabel && <Tag color={statusColor} className="p9101-status-tag">{statusLabel}</Tag>}
        </div>

        {/* Quick Search */}
        <div className="p9101-search-bar">
          <Input
            placeholder="輸入代號或姓名搜尋"
            value={searchKey}
            onChange={(e) => setSearchKey(e.target.value)}
            onPressEnter={() => quickSearch(searchKey)}
            style={{ width: 200 }}
            disabled={isEditing}
          />
          <Button icon={<SearchOutlined />} onClick={() => quickSearch(searchKey)} disabled={isEditing}>搜尋</Button>
          <span className="p9101-record-info">{recordPosition} / {recordCount}</span>
        </div>

        {/* Navigation */}
        <div className="p9101-nav-bar">
          <Button icon={<StepBackwardOutlined />} onClick={navFirst} disabled={isEditing}>首筆</Button>
          <Button icon={<CaretLeftOutlined />} onClick={navPrev} disabled={isEditing}>前筆</Button>
          <Button icon={<CaretRightOutlined />} onClick={navNext} disabled={isEditing}>次筆</Button>
          <Button icon={<StepForwardOutlined />} onClick={navLast} disabled={isEditing}>末筆</Button>
        </div>

        <Tabs defaultActiveKey="basic" items={[
          {
            key: 'basic',
            label: '基本資料',
            children: (
              <div className="p9101-section">
                <div className="p9101-fields">
                  <div className="p9101-field-row">
                    <label>員工代號</label>
                    <Input value={f901_01} onChange={(e) => setF901_01(e.target.value)} maxLength={8} disabled={mode !== 'add'} style={{ width: 120 }} />
                  </div>
                  <div className="p9101-field-row">
                    <label>姓名</label>
                    <Input value={f901_11} onChange={(e) => setF901_11(e.target.value)} maxLength={12} disabled={!isEditing} style={{ width: 160 }} />
                  </div>
                  <div className="p9101-field-row">
                    <label>英文名</label>
                    <Input value={f901_55} onChange={(e) => setF901_55(e.target.value)} maxLength={30} disabled={!isEditing} style={{ width: 200 }} />
                  </div>
                  <div className="p9101-field-row">
                    <label>身份證號</label>
                    <Input value={f901_12} onChange={(e) => setF901_12(e.target.value)} maxLength={20} disabled={!isEditing} style={{ width: 200 }} />
                  </div>
                  <div className="p9101-field-row">
                    <label>生日</label>
                    <DatePicker
                      value={f901_13 ? dayjs(f901_13) : null}
                      onChange={(d) => setF901_13(d ? d.format('YYYY-MM-DD') : null)}
                      disabled={!isEditing}
                      style={{ width: 160 }}
                    />
                  </div>
                  <div className="p9101-field-row">
                    <label>性別</label>
                    <Radio.Group value={f901_14} onChange={(e) => setF901_14(e.target.value)} disabled={!isEditing}>
                      <Radio value={1}>男</Radio>
                      <Radio value={2}>女</Radio>
                    </Radio.Group>
                  </div>
                  <div className="p9101-field-row">
                    <label>聯絡電話</label>
                    <Input value={f901_19} onChange={(e) => setF901_19(e.target.value)} maxLength={20} disabled={!isEditing} style={{ width: 160 }} />
                  </div>
                  <div className="p9101-field-row">
                    <label>行動電話</label>
                    <Input value={f901_20} onChange={(e) => setF901_20(e.target.value)} maxLength={20} disabled={!isEditing} style={{ width: 160 }} />
                  </div>
                  <div className="p9101-field-row p9101-field-full">
                    <label>聯絡地址</label>
                    <Input value={f901_25} onChange={(e) => setF901_25(e.target.value)} maxLength={60} disabled={!isEditing} style={{ width: 400 }} />
                  </div>
                  <div className="p9101-field-row p9101-field-full">
                    <label>戶籍地址</label>
                    <Input value={f901_26} onChange={(e) => setF901_26(e.target.value)} maxLength={60} disabled={!isEditing} style={{ width: 400 }} />
                  </div>
                  <div className="p9101-field-row">
                    <label>緊急聯絡人</label>
                    <Input value={f901_27} onChange={(e) => setF901_27(e.target.value)} maxLength={12} disabled={!isEditing} style={{ width: 120 }} />
                  </div>
                  <div className="p9101-field-row">
                    <label>緊急電話</label>
                    <Input value={f901_28} onChange={(e) => setF901_28(e.target.value)} maxLength={15} disabled={!isEditing} style={{ width: 160 }} />
                  </div>
                  <div className="p9101-field-row">
                    <label>郵件地址</label>
                    <Input value={f901_52} onChange={(e) => setF901_52(e.target.value)} maxLength={40} disabled={!isEditing} style={{ width: 300 }} />
                  </div>
                </div>
              </div>
            ),
          },
          {
            key: 'employment',
            label: '任職資料',
            children: (
              <div className="p9101-section">
                <div className="p9101-fields">
                  <div className="p9101-field-row">
                    <label>公司名稱</label>
                    <Input value={f901_30} onChange={(e) => setF901_30(e.target.value)} maxLength={30} disabled={!isEditing} style={{ width: 200 }} />
                  </div>
                  <div className="p9101-field-row">
                    <label>部門</label>
                    <Input value={f901_32} onChange={(e) => setF901_32(e.target.value)} maxLength={4} disabled={!isEditing} style={{ width: 100 }} />
                  </div>
                  <div className="p9101-field-row">
                    <label>職稱</label>
                    <Input value={f901_33} onChange={(e) => setF901_33(e.target.value)} maxLength={4} disabled={!isEditing} style={{ width: 100 }} />
                  </div>
                  <div className="p9101-field-row">
                    <label>職等</label>
                    <Input value={f901_37} onChange={(e) => setF901_37(e.target.value)} maxLength={4} disabled={!isEditing} style={{ width: 100 }} />
                  </div>
                  <div className="p9101-field-row">
                    <label>到職日</label>
                    <DatePicker
                      value={f901_31 ? dayjs(f901_31) : null}
                      onChange={(d) => setF901_31(d ? d.format('YYYY-MM-DD') : null)}
                      disabled={!isEditing}
                      style={{ width: 160 }}
                    />
                  </div>
                  <div className="p9101-field-row">
                    <label>轉正日</label>
                    <DatePicker
                      value={f901_35 ? dayjs(f901_35) : null}
                      onChange={(d) => setF901_35(d ? d.format('YYYY-MM-DD') : null)}
                      disabled={!isEditing}
                      style={{ width: 160 }}
                    />
                  </div>
                  <div className="p9101-field-row">
                    <label>離職日</label>
                    <DatePicker
                      value={f901_34 ? dayjs(f901_34) : null}
                      onChange={(d) => setF901_34(d ? d.format('YYYY-MM-DD') : null)}
                      disabled={!isEditing}
                      style={{ width: 160 }}
                    />
                  </div>
                  <div className="p9101-field-row">
                    <label>正式員工</label>
                    <Checkbox checked={f901_60} onChange={(e) => setF901_60(e.target.checked)} disabled={!isEditing} />
                  </div>
                </div>
              </div>
            ),
          },
          {
            key: 'audit',
            label: '系統資訊',
            children: (
              <div className="p9101-section">
                <div className="p9101-fields">
                  <div className="p9101-field-row">
                    <label>建立人員</label>
                    <Input value={f901_91} disabled style={{ width: 120 }} />
                  </div>
                  <div className="p9101-field-row">
                    <label>建立日期</label>
                    <Input value={f901_92 ? f901_92.substring(0, 10) : ''} disabled style={{ width: 140 }} />
                  </div>
                </div>
              </div>
            ),
          },
        ]} />

        {/* Action Buttons */}
        <div className="p9101-actions">
          {!isEditing ? (
            <>
              <Button type="primary" icon={<PlusOutlined />} onClick={startAdd}>新增</Button>
              <Button icon={<EditOutlined />} onClick={startEdit} disabled={recordCount === 0}>修改</Button>
              <Button
                danger
                icon={<DeleteOutlined />}
                disabled={recordCount === 0}
                onClick={() => {
                  Modal.confirm({
                    title: '確認刪除',
                    content: `確定要刪除員工 ${f901_01} (${f901_11}) 嗎？`,
                    onOk: doDelete,
                  });
                }}
              >
                刪除
              </Button>
            </>
          ) : (
            <>
              <Button type="primary" icon={<SaveOutlined />} onClick={save}>存檔</Button>
              <Button icon={<CloseOutlined />} onClick={cancel}>還原</Button>
            </>
          )}
        </div>
      </div>
    </Spin>
  );
};

export default P9101Form;
